import {
  NewConstructionData,
  PropertyCategoryPremium,
  RegionalActivity,
} from './NewVsResaleChart.types'
import {
  formatPrice as formatCentralizedPrice,
  formatPricePerSqm as formatCentralizedPricePerSqm,
  formatPercentage as formatCentralizedPercentage,
  formatQuarter as formatCentralizedQuarter,
} from '../../../utils/number/formatUtils'

export const formatPrice = (
  price: number,
  isRental: boolean = false
): string => {
  return formatCentralizedPrice(price, isRental)
}

export const formatPricePerSqm = (price: number): string => {
  return formatCentralizedPricePerSqm(price)
}

export const formatQuarter = (dateString: string): string => {
  return formatCentralizedQuarter(dateString)
}

export const formatPercentage = (value: number): string => {
  return formatCentralizedPercentage(value)
}

export const processTimeSeriesData = (
  aggregations: any
): NewConstructionData[] => {
  if (!aggregations?.over_time?.buckets) {
    return []
  }

  return aggregations.over_time.buckets
    .filter(
      (bucket: any) =>
        bucket.new_construction?.count?.value > 0 ||
        bucket.existing?.count?.value > 0
    )
    .map((bucket: any) => ({
      date: bucket.key_as_string,
      newCount: bucket.new_construction?.count?.value || 0,
      existingCount: bucket.existing?.count?.value || 0,
      newAvgPrice: bucket.new_construction?.avg_price?.value || 0,
      existingAvgPrice: bucket.existing?.avg_price?.value || 0,
    }))
}

export const processPropertyCategoryData = (
  aggregations: any,
  priceType: 'median' | 'average' = 'median'
): PropertyCategoryPremium[] => {
  if (!aggregations?.by_property_category?.buckets) {
    return []
  }

  return aggregations.by_property_category.buckets
    .filter((bucket: any) => {
      const hasData =
        bucket.new_construction?.count?.value > 10 &&
        bucket.existing?.count?.value > 10
      if (!hasData) return false

      if (priceType === 'median') {
        return (
          bucket.new_construction?.median_price_per_sqm?.values?.['50.0'] > 0 &&
          bucket.existing?.median_price_per_sqm?.values?.['50.0'] > 0
        )
      } else {
        return (
          bucket.new_construction?.avg_price_per_sqm?.value > 0 &&
          bucket.existing?.avg_price_per_sqm?.value > 0
        )
      }
    })
    .map((bucket: any) => {
      const newPriceSqm =
        priceType === 'median'
          ? bucket.new_construction.median_price_per_sqm.values['50.0']
          : bucket.new_construction.avg_price_per_sqm.value
      const existingPriceSqm =
        priceType === 'median'
          ? bucket.existing.median_price_per_sqm.values['50.0']
          : bucket.existing.avg_price_per_sqm.value
      const premium =
        ((newPriceSqm - existingPriceSqm) / existingPriceSqm) * 100

      return {
        category: bucket.key,
        newPriceSqm,
        existingPriceSqm,
        premium,
        newCount: bucket.new_construction.count.value,
        existingCount: bucket.existing.count.value,
      }
    })
    .sort(
      (a: PropertyCategoryPremium, b: PropertyCategoryPremium) =>
        b.premium - a.premium
    )
}

export const processRegionalData = (
  aggregations: any,
  selectedRegion?: string
): RegionalActivity[] => {
  // Use municipalities if a region is selected, otherwise use cantons
  const buckets =
    selectedRegion && aggregations.by_municipality?.buckets
      ? aggregations.by_municipality.buckets
      : aggregations.by_canton?.buckets || []

  return buckets
    .map((bucket: any) => {
      const newCount = bucket.new_construction?.count?.value || 0
      const existingCount = bucket.existing?.count?.value || 0
      const total = newCount + existingCount

      return {
        name: bucket.key,
        newCount,
        existingCount,
        newPercentage: total > 0 ? (newCount / total) * 100 : 0,
      }
    })
    .sort(
      (a: RegionalActivity, b: RegionalActivity) =>
        b.newPercentage - a.newPercentage
    )
    .slice(0, 20) // Top 20 regions
}

export const calculateKPIs = (
  aggregations: any,
  priceType: 'median' | 'average' = 'median'
) => {
  const totalNew = aggregations.total_new?.doc_count || 0
  const totalExisting = aggregations.total_existing?.doc_count || 0
  const total = totalNew + totalExisting
  const newPercentage = total > 0 ? (totalNew / total) * 100 : 0

  const avgPriceSqmNew =
    priceType === 'median'
      ? aggregations.avg_price_per_sqm_new?.median_price_per_sqm?.values?.[
          '50.0'
        ] || 0
      : aggregations.avg_price_per_sqm_new?.avg_price_per_sqm?.value || 0
  const avgPriceSqmExisting =
    priceType === 'median'
      ? aggregations.avg_price_per_sqm_existing?.median_price_per_sqm?.values?.[
          '50.0'
        ] || 0
      : aggregations.avg_price_per_sqm_existing?.avg_price_per_sqm?.value || 0
  const premium =
    avgPriceSqmExisting > 0
      ? ((avgPriceSqmNew - avgPriceSqmExisting) / avgPriceSqmExisting) * 100
      : 0

  return {
    newPercentage,
    avgPriceSqmNew,
    avgPriceSqmExisting,
    premium,
    totalNew,
    totalExisting,
    total,
  }
}

export const getChartColors = () => ({
  new: '#2563eb',
  newLight: '#60a5fa',
  existing: '#10b981',
  existingLight: '#6ee7b7',
})

export interface Insight {
  title: string
  description: string
}

type TranslationFn = (key: string, options?: Record<string, unknown>) => string

const defaultT: TranslationFn = (key: string) => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

export const generateInsights = (
  timeSeriesData: NewConstructionData[],
  categoryData: PropertyCategoryPremium[],
  regionalData: RegionalActivity[],
  kpis: ReturnType<typeof calculateKPIs>,
  priceType: 'median' | 'average' = 'median',
  t: TranslationFn = defaultT
): Insight[] => {
  const insights: Insight[] = []

  // 1. Overall market composition
  insights.push({
    title: t('charts.insights.newVsResale.marketComposition'),
    description: t('charts.insights.descriptions.newVsResale.marketComposition', {
      newPercentage: kpis.newPercentage.toFixed(1),
      totalNew: kpis.totalNew.toLocaleString(),
      existingPercentage: (100 - kpis.newPercentage).toFixed(1),
      totalExisting: kpis.totalExisting.toLocaleString(),
    }),
  })

  // 2. Premium analysis
  if (kpis.premium !== 0) {
    const descriptionKey =
      kpis.premium > 0
        ? 'charts.insights.descriptions.newVsResale.constructionPremiumPositive'
        : 'charts.insights.descriptions.newVsResale.constructionPremiumNegative'
    insights.push({
      title: t('charts.insights.newVsResale.constructionPremium'),
      description: t(descriptionKey, {
        premium: Math.abs(kpis.premium).toFixed(1),
        discount: Math.abs(kpis.premium).toFixed(1),
        priceType,
        newPrice: formatPricePerSqm(kpis.avgPriceSqmNew),
        existingPrice: formatPricePerSqm(kpis.avgPriceSqmExisting),
      }),
    })
  }

  // 3. Trend analysis from time series
  if (timeSeriesData.length >= 4) {
    const recentData = timeSeriesData.slice(-4)
    const earlierData = timeSeriesData.slice(0, 4)

    const recentNewShare =
      (recentData.reduce((sum, d) => sum + d.newCount, 0) /
        recentData.reduce((sum, d) => sum + d.newCount + d.existingCount, 0)) *
      100
    const earlierNewShare =
      (earlierData.reduce((sum, d) => sum + d.newCount, 0) /
        earlierData.reduce((sum, d) => sum + d.newCount + d.existingCount, 0)) *
      100

    const trend = recentNewShare - earlierNewShare
    const startPeriod = formatQuarter(earlierData[0].date)
    const endPeriod = formatQuarter(recentData[recentData.length - 1].date)

    if (Math.abs(trend) < 1) {
      insights.push({
        title: t('charts.insights.marketTrend'),
        description: t('charts.insights.descriptions.newVsResale.marketTrendStable', {
          startPeriod,
          endPeriod,
        }),
      })
    } else {
      insights.push({
        title: t('charts.insights.marketTrend'),
        description: t('charts.insights.descriptions.newVsResale.marketTrendChanged', {
          direction: trend > 0 ? t('charts.common.increased') || 'increased' : t('charts.common.decreased') || 'decreased',
          change: Math.abs(trend).toFixed(1),
          startPeriod,
          endPeriod,
        }),
      })
    }
  }

  // 4. Category premium patterns
  if (categoryData.length >= 2) {
    const highestPremium = categoryData[0]
    const lowestPremium = categoryData[categoryData.length - 1]

    const lowPremiumText =
      lowestPremium.premium > 0
        ? `${t('charts.common.smallestPremium') || 'the smallest premium at'} ${lowestPremium.premium.toFixed(1)}%`
        : `${t('charts.common.discount') || 'a discount of'} ${Math.abs(lowestPremium.premium).toFixed(1)}%`

    insights.push({
      title: t('charts.insights.newVsResale.premiumVariations'),
      description: t('charts.insights.descriptions.newVsResale.premiumVariations', {
        highCategory: highestPremium.category,
        highPremium: highestPremium.premium.toFixed(1),
        lowCategory: lowestPremium.category,
        lowPremiumText,
      }),
    })
  }

  // 5. Regional concentration
  if (regionalData.length > 0) {
    const topNewMarkets = regionalData.slice(0, 3)
    const avgNewShare =
      regionalData.reduce((sum, r) => sum + r.newPercentage, 0) /
      regionalData.length

    insights.push({
      title: t('charts.insights.newVsResale.regionalActivity'),
      description: t('charts.insights.descriptions.newVsResale.regionalActivity', {
        topRegion: topNewMarkets[0].name,
        topPercentage: topNewMarkets[0].newPercentage.toFixed(1),
        secondRegion: topNewMarkets[1]?.name || t('charts.common.otherRegions') || 'other regions',
        avgPercentage: avgNewShare.toFixed(1),
      }),
    })
  }

  return insights
}
